import { useEffect, useState } from 'react';
import { fetchKnowledgeLibraryStatusFailClosed, type KnowledgeLibraryStatus } from '@/api/knowledgeLibrary';

/**
 * ADR-036 — reads the Library's `/status` once per tenant for the component that mounts it.
 *
 * Deliberately NOT a global hook or store: only the Library nav entry, the Library page and the bank page's
 * "From the Library" tab call it, so a test that mounts something else never sees a new request. Fails closed:
 * until the answer arrives, and after any error, `status.enabled` is false and every Library surface stays
 * hidden.
 */
export function useKnowledgeLibraryStatus(tenantId: string): { loading: boolean; status: KnowledgeLibraryStatus } {
  const [state, setState] = useState<{ tenantId: string; status: KnowledgeLibraryStatus } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let status: KnowledgeLibraryStatus = OFF;
      try {
        const s = await fetchKnowledgeLibraryStatusFailClosed(tenantId);
        if (s && s.enabled === true) status = s;
      } catch {
        // fail closed — `status` stays OFF
      }
      if (!cancelled) setState({ tenantId, status });
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  // A status fetched for a previous tenant is never shown for the current one (platform-admin tenant switch).
  if (!state || state.tenantId !== tenantId) {
    return { loading: true, status: OFF };
  }
  return { loading: false, status: state.status };
}

const OFF: KnowledgeLibraryStatus = Object.freeze({ enabled: false, libraryBankId: null, autoGradeBanks: false });
